// Phase B — Welfare Approvals Inbox.
//
// Three tabs in one page:
//   1. Room Allocations (HR queue) — approve/reject
//   2. Ration — GM stage    — GM approves to advance to HR
//   3. Ration — HR stage    — HR finalizes (or rejects)
//
// Tab counts visible as badges so the page is self-explanatory.
// Approve/reject buttons call welfareAssignmentApi which posts to
// /welfare-assign/* — already deployed on backend.

import { useMemo, useState } from 'react'
import {
  App, Badge, Button, Card, Modal, Popconfirm, Space, Table, Tabs, Tag,
  Typography, Input, Form,
} from 'antd'
import {
  CheckOutlined, CloseOutlined, HomeOutlined, ShoppingCartOutlined,
  UserOutlined,
} from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'

import {
  welfareAssignmentApi,
  type RationInboxRow, type RoomInboxRow,
} from '@/features/hr/welfareAssignmentApi'
import {
  welfareMastersApi, type Colony, type RationRule, type Room,
} from '@/features/hr/welfareMastersApi'
import PageHeader from '@/shared/components/PageHeader'
import { usePlantId } from '@/shared/hooks/usePlantId'

const { Text } = Typography


export default function WelfareApprovalsInboxPage() {
  const { plantId } = usePlantId()

  const { data: roomRows, isLoading: roomsLoading } = useQuery({
    queryKey: ['welfare-inbox-rooms', plantId],
    queryFn: () => welfareAssignmentApi.inboxRooms(plantId ?? undefined),
    enabled: !!plantId,
  })
  const { data: gmRows, isLoading: gmLoading } = useQuery({
    queryKey: ['welfare-inbox-ration-gm', plantId],
    queryFn: () => welfareAssignmentApi.inboxRationGm(plantId ?? undefined),
    enabled: !!plantId,
  })
  const { data: hrRows, isLoading: hrLoading } = useQuery({
    queryKey: ['welfare-inbox-ration-hr', plantId],
    queryFn: () => welfareAssignmentApi.inboxRationHr(plantId ?? undefined),
    enabled: !!plantId,
  })

  return (
    <div>
      <PageHeader
        title="Welfare Approvals"
        subtitle="Room allocations (HR) and ration sanctions (GM → HR). Click into a row to approve or reject."
      />
      <Card style={{ marginTop: 16 }}>
        <Tabs
          items={[
            {
              key: 'rooms',
              label: (
                <Space>
                  <HomeOutlined />
                  Room Allocations
                  <Badge count={roomRows?.length ?? 0} showZero color="#1677ff" />
                </Space>
              ),
              children: (
                <RoomInbox rows={roomRows ?? []} loading={roomsLoading} />
              ),
            },
            {
              key: 'ration-gm',
              label: (
                <Space>
                  <ShoppingCartOutlined />
                  Ration — GM Approval
                  <Badge count={gmRows?.length ?? 0} showZero color="#fa8c16" />
                </Space>
              ),
              children: (
                <RationInbox rows={gmRows ?? []} loading={gmLoading} stage="GM" />
              ),
            },
            {
              key: 'ration-hr',
              label: (
                <Space>
                  <ShoppingCartOutlined />
                  Ration — HR Approval
                  <Badge count={hrRows?.length ?? 0} showZero color="#52c41a" />
                </Space>
              ),
              children: (
                <RationInbox rows={hrRows ?? []} loading={hrLoading} stage="HR" />
              ),
            },
          ]}
        />
      </Card>
    </div>
  )
}


// ─── Room Inbox tab ────────────────────────────────────────────────────

function RoomInbox({
  rows, loading,
}: { rows: RoomInboxRow[]; loading: boolean }) {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const [rejecting, setRejecting] = useState<RoomInboxRow | null>(null)

  const { data: colonies } = useQuery({
    queryKey: ['colonies-for-inbox'],
    queryFn: () => welfareMastersApi.listColonies(),
  })
  const { data: rooms } = useQuery({
    queryKey: ['rooms-for-inbox'],
    queryFn: () => welfareMastersApi.listRooms(),
  })

  const coloniesById = useMemo(() => {
    const m = new Map<string, Colony>()
    ;(colonies ?? []).forEach((c) => m.set(c.id, c))
    return m
  }, [colonies])
  const roomsById = useMemo(() => {
    const m = new Map<string, Room>()
    ;(rooms ?? []).forEach((r) => m.set(r.id, r))
    return m
  }, [rooms])

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['welfare-inbox-rooms'] })
    qc.invalidateQueries({ queryKey: ['rooms'] })
    qc.invalidateQueries({ queryKey: ['beds'] })
  }

  const approveMut = useMutation({
    mutationFn: (employeeId: string) =>
      welfareAssignmentApi.approveRoom(employeeId),
    onSuccess: () => { message.success('Approved'); invalidate() },
    onError: errToast(message),
  })
  const rejectMut = useMutation({
    mutationFn: ({ employeeId, reason }: { employeeId: string; reason: string }) =>
      welfareAssignmentApi.rejectRoom(employeeId, reason),
    onSuccess: () => { message.success('Rejected'); invalidate(); setRejecting(null) },
    onError: errToast(message),
  })

  return (
    <>
      <Table
        rowKey="employee_id"
        size="small"
        loading={loading}
        pagination={{ pageSize: 25 }}
        locale={{ emptyText: 'No pending room allocations 🎉' }}
        columns={[
          {
            title: 'Employee', key: 'emp',
            render: (_: unknown, r: RoomInboxRow) => (
              <Space direction="vertical" size={2}>
                <Space size={4}>
                  <UserOutlined style={{ color: '#1677ff' }} />
                  <Text strong>{r.emp_name}</Text>
                </Space>
                <Space size={4}>
                  <Text code style={{ fontSize: 10 }}>{r.emp_code}</Text>
                  <Tag>{r.category}</Tag>
                </Space>
              </Space>
            ),
          },
          {
            title: 'Room', key: 'room',
            render: (_: unknown, r: RoomInboxRow) => {
              const colony = r.colony_id ? coloniesById.get(r.colony_id) : null
              const room = r.room_id ? roomsById.get(r.room_id) : null
              return (
                <Space direction="vertical" size={2}>
                  <Text strong>{colony?.colony_name ?? '—'}</Text>
                  {room && (
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      {room.building_block ? `${room.building_block} / ` : ''}
                      {room.room_no} · {room.room_type.replace('_', ' ')}
                    </Text>
                  )}
                </Space>
              )
            },
          },
          {
            title: 'Start date', dataIndex: 'allocation_start_date', key: 'sd',
            width: 120,
            render: (v: string | null) =>
              v ? dayjs(v).format('DD MMM YYYY') : '—',
          },
          {
            title: 'Rent / Deposit', key: 'money', width: 160,
            render: (_: unknown, r: RoomInboxRow) => (
              <Space direction="vertical" size={2}>
                <Text>Rent: ₹{r.rent_amount ? Number(r.rent_amount).toLocaleString('en-IN') : '0'}/mo</Text>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  Deposit: ₹{r.security_deposit ? Number(r.security_deposit).toLocaleString('en-IN') : '0'}
                </Text>
              </Space>
            ),
          },
          {
            title: 'Requested', dataIndex: 'updated_at', key: 'rq', width: 120,
            render: (v: string | null) =>
              v ? <Text type="secondary" style={{ fontSize: 11 }}>{dayjs(v).fromNow()}</Text> : '—',
          },
          {
            title: 'Action', key: 'act', width: 180,
            render: (_: unknown, r: RoomInboxRow) => (
              <Space>
                <Popconfirm
                  title="Approve room allocation?"
                  description="Bed will be marked OCCUPIED. Cannot be undone via this screen."
                  onConfirm={() => approveMut.mutate(r.employee_id)}>
                  <Button size="small" type="primary" icon={<CheckOutlined />}>
                    Approve
                  </Button>
                </Popconfirm>
                <Button size="small" danger icon={<CloseOutlined />}
                  onClick={() => setRejecting(r)}>
                  Reject
                </Button>
              </Space>
            ),
          },
        ] as never}
        dataSource={rows}
      />
      <RejectModal
        open={!!rejecting}
        target={rejecting ? `${rejecting.emp_name} (${rejecting.emp_code})` : ''}
        onClose={() => setRejecting(null)}
        onSubmit={(reason) => rejecting && rejectMut.mutate({
          employeeId: rejecting.employee_id, reason,
        })}
        submitting={rejectMut.isPending}
      />
    </>
  )
}


// ─── Ration Inbox tab (works for both GM and HR stages) ───────────────

function RationInbox({
  rows, loading, stage,
}: { rows: RationInboxRow[]; loading: boolean; stage: 'GM' | 'HR' }) {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const [rejecting, setRejecting] = useState<RationInboxRow | null>(null)

  const { data: rules } = useQuery({
    queryKey: ['ration-rules-for-inbox'],
    queryFn: () => welfareMastersApi.listRationRules(),
  })
  const rulesById = useMemo(() => {
    const m = new Map<string, RationRule>()
    ;(rules ?? []).forEach((r) => m.set(r.id, r))
    return m
  }, [rules])

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['welfare-inbox-ration-gm'] })
    qc.invalidateQueries({ queryKey: ['welfare-inbox-ration-hr'] })
  }

  const approveMut = useMutation({
    mutationFn: (employeeId: string) =>
      stage === 'GM'
        ? welfareAssignmentApi.sanctionRationGm(employeeId)
        : welfareAssignmentApi.sanctionRationHr(employeeId),
    onSuccess: () => {
      message.success(stage === 'GM'
        ? 'GM approved — moved to HR queue'
        : 'HR approved — ration SANCTIONED')
      invalidate()
    },
    onError: errToast(message),
  })
  const rejectMut = useMutation({
    mutationFn: ({ employeeId, reason }: { employeeId: string; reason: string }) =>
      welfareAssignmentApi.rejectRation(employeeId, stage, reason),
    onSuccess: () => { message.success('Rejected'); invalidate(); setRejecting(null) },
    onError: errToast(message),
  })

  return (
    <>
      <Table
        rowKey="employee_id"
        size="small"
        loading={loading}
        pagination={{ pageSize: 25 }}
        locale={{
          emptyText: stage === 'GM'
            ? 'No ration requests awaiting GM approval 🎉'
            : 'No GM-approved rations awaiting HR finalization 🎉',
        }}
        columns={[
          {
            title: 'Employee', key: 'emp',
            render: (_: unknown, r: RationInboxRow) => (
              <Space direction="vertical" size={2}>
                <Space size={4}>
                  <UserOutlined style={{ color: '#1677ff' }} />
                  <Text strong>{r.emp_name}</Text>
                </Space>
                <Space size={4}>
                  <Text code style={{ fontSize: 10 }}>{r.emp_code}</Text>
                  <Tag>{r.category}</Tag>
                </Space>
              </Space>
            ),
          },
          {
            title: 'Ration Rule', key: 'rule',
            render: (_: unknown, r: RationInboxRow) => {
              const rule = r.ration_allowance_rule_id
                ? rulesById.get(r.ration_allowance_rule_id) : null
              if (!rule) return <Text type="secondary">—</Text>
              return (
                <Space direction="vertical" size={2}>
                  <Text code style={{ fontSize: 11 }}>{rule.rule_code}</Text>
                  {rule.rule_name && (
                    <Text type="secondary" style={{ fontSize: 11 }}>{rule.rule_name}</Text>
                  )}
                </Space>
              )
            },
          },
          {
            title: 'Amount', key: 'amt',
            render: (_: unknown, r: RationInboxRow) => {
              const rule = r.ration_allowance_rule_id
                ? rulesById.get(r.ration_allowance_rule_id) : null
              const override = r.ration_amount_override
              const weekly = override ?? rule?.weekly_amount ?? rule?.monthly_amount
              if (!weekly) return <Text type="secondary">—</Text>
              return (
                <Space direction="vertical" size={2}>
                  <Text strong>₹{Number(weekly).toLocaleString('en-IN')}</Text>
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    {rule?.payment_frequency.toLowerCase()}
                    {override && ' (override)'}
                  </Text>
                </Space>
              )
            },
          },
          {
            title: 'Effective from', dataIndex: 'ration_effective_from',
            key: 'ef', width: 120,
            render: (v: string | null) =>
              v ? dayjs(v).format('DD MMM YYYY') : '—',
          },
          ...(stage === 'HR' ? [{
            title: 'GM approved', dataIndex: 'ration_sanctioned_by_gm_at', key: 'gm',
            width: 120,
            render: (v: string | null) =>
              v ? (
                <Tag color="green" style={{ margin: 0 }}>
                  {dayjs(v).fromNow()}
                </Tag>
              ) : '—',
          }] : []),
          {
            title: 'Action', key: 'act', width: 220,
            render: (_: unknown, r: RationInboxRow) => (
              <Space>
                <Popconfirm
                  title={stage === 'GM'
                    ? 'Approve as GM? Will move to HR queue.'
                    : 'Finalize? Ration cycle starts from effective date.'}
                  onConfirm={() => approveMut.mutate(r.employee_id)}>
                  <Button size="small" type="primary" icon={<CheckOutlined />}>
                    {stage === 'GM' ? 'GM Approve' : 'HR Finalize'}
                  </Button>
                </Popconfirm>
                <Button size="small" danger icon={<CloseOutlined />}
                  onClick={() => setRejecting(r)}>
                  Reject
                </Button>
              </Space>
            ),
          },
        ] as never}
        dataSource={rows}
      />
      <RejectModal
        open={!!rejecting}
        target={rejecting ? `${rejecting.emp_name} (${rejecting.emp_code})` : ''}
        onClose={() => setRejecting(null)}
        onSubmit={(reason) => rejecting && rejectMut.mutate({
          employeeId: rejecting.employee_id, reason,
        })}
        submitting={rejectMut.isPending}
      />
    </>
  )
}


// ─── Reject reason modal (shared) ─────────────────────────────────────

function RejectModal({
  open, target, onClose, onSubmit, submitting,
}: {
  open: boolean
  target: string
  onClose: () => void
  onSubmit: (reason: string) => void
  submitting: boolean
}) {
  const [form] = Form.useForm()
  return (
    <Modal
      open={open} onCancel={onClose} destroyOnClose
      title={`Reject — ${target}`}
      okText="Reject" okButtonProps={{ danger: true }}
      confirmLoading={submitting}
      onOk={() => form.validateFields().then((v) => onSubmit(v.reason))}
    >
      <Form form={form} layout="vertical">
        <Form.Item name="reason" label="Reason for rejection"
          rules={[{ required: true, min: 5,
            message: 'Provide at least 5 characters of context' }]}>
          <Input.TextArea rows={3}
            placeholder="e.g. Room mismatch — worker requested family quarter but allocated dorm" />
        </Form.Item>
      </Form>
    </Modal>
  )
}


// ─── Helpers ──────────────────────────────────────────────────────────

function errToast(message: ReturnType<typeof App.useApp>['message']) {
  return (e: unknown) => {
    const err = e as { response?: { data?: { detail?: string } } }
    message.error(err?.response?.data?.detail ?? 'Action failed')
  }
}
